<template>
    <div class="flex flex-col gap-4 w-full">
  
      <div v-for="(level, index) in activeReasonLevels" :key="level.id" class="w-full">
        <SimpleReasonSelect
          :options="level.options"
          :placeholder="level.placeholder"
          v-model="level.selected"
          @selection-made="handleSelectionMade(index, $event)"
        />
      </div>
    </div>
  </template>
  
  <script>
  import SimpleReasonSelect from './SimpleReasonSelect.vue';
  
  export default {
    name: 'ReasonSelectionManager',
    components: {
      SimpleReasonSelect,
    },
    props: {
      // The full, initial nested reasons data structure
      initialReasons: {
        type: Array,
        required: true,
      },
      // Optional: initial value to pre-select a path
      initialModelValue: {
        type: Object,
        default: null
      }
    },
    data() {
      return {
        // This array will hold the configuration for each active select level
        // Each item: { id: uniqueId, options: Array, selected: Object | null, placeholder: string }
        activeReasonLevels: [],
        // This array stores the path of selected reasons (from root to current level)
        // Each item: the selected reason object itself
        selectedPath: [],
        // The final selected leaf node (the equivalent of the old modelValue)
        selectedLeaf: null,
      };
    },
    computed: {
      fullPathName() {
        return this.selectedPath.map(item => item.name).join(' - ');
      }
    },
    mounted() {
        // Initialize the first level
        this.addLevel(this.initialReasons, 'Select top-level reason');
  
        // If an initial model value is provided, try to populate the path
        if (this.initialModelValue) {
          this.populateFromModelValue(this.initialModelValue);
        }
    },
    methods: {
      addLevel(options, placeholder = 'Select an option') {
        const newId = Date.now() + Math.random(); // Simple unique ID
        this.activeReasonLevels.push({
          id: newId,
          options: options.filter(item => item.enabled),
          selected: null, // No option selected initially for this new level
          placeholder: placeholder,
        });
      },
  
      removeLevelsStartingFromIndex(startIndex) {
        if (startIndex < this.activeReasonLevels.length) {
          this.activeReasonLevels.splice(startIndex);
          this.selectedPath.splice(startIndex);
        }
      },
  
      handleSelectionMade(index, selectedOption) {
        // Clear all levels after the current one
        this.removeLevelsStartingFromIndex(index + 1);
  
        // Update the selected path
        this.selectedPath[index] = selectedOption;
        this.selectedPath.splice(index + 1); // Ensure no old elements if a parent was changed
  
        // If a new option is selected at this level, update its 'selected' property
        this.activeReasonLevels[index].selected = selectedOption;
  
        // Determine if we need to add a new level
        if (selectedOption && selectedOption.children && selectedOption.children.length > 0) {
          this.addLevel(selectedOption.children, `Select sub-reason for ${selectedOption.name}`);
          this.selectedLeaf = null; // Reset leaf if we're moving deeper
        } else {
          // This is a leaf node, so it's our final selection
          this.selectedLeaf = selectedOption;
        }
        // Optionally emit the final selectedLeaf
        this.$emit('update:modelValue', this.selectedLeaf);
      },
  
      clearAllSelections() {
        this.activeReasonLevels = [];
        this.selectedPath = [];
        this.selectedLeaf = null;
        this.addLevel(this.initialReasons, 'Select top-level reason'); // Re-add the first level
        this.$emit('update:modelValue', null); // Emit null for the final model value
      },
  
      // Method to pre-populate selection based on an initial model value
      populateFromModelValue(leafNode) {
        if (!leafNode || !this.initialReasons) return;
  
        // let currentOptions = this.initialReasons;
        // let currentPath = [];
        let foundPath = [];
        // let currentSelected = null;
  
        // Recursive helper to find the path to the leaf node
        const findPath = (options, targetId, pathSoFar = []) => {
          for (const option of options) {
            const newPath = [...pathSoFar, option];
            if (option.id === targetId) {
              return newPath;
            }
            if (option.children && option.children.length > 0) {
              const result = findPath(option.children, targetId, newPath);
              if (result) {
                return result;
              }
            }
          }
          return null;
        };
  
        foundPath = findPath(this.initialReasons, leafNode.id);
  
        if (foundPath) {
          this.clearAllSelections(); // Clear existing levels first
          let optionsForNextLevel = this.initialReasons;
  
          foundPath.forEach((node, index) => {
            // Add the current level's select
            this.addLevel(optionsForNextLevel, `Select reason ${index + 1}`);
            const currentLevelConfig = this.activeReasonLevels[index];
            currentLevelConfig.selected = node;
            this.selectedPath[index] = node;
  
            if (node.children && node.children.length > 0) {
              optionsForNextLevel = node.children;
            } else {
              // This is the leaf node
              this.selectedLeaf = node;
            }
          });
          this.$emit('update:modelValue', this.selectedLeaf);
        }
      }
    },
  };
  </script>